import { useState, useEffect } from "react";
import { format } from "date-fns";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface MonthlyListProps<T> {
  items: T[];
  renderItem: (item: T) => JSX.Element;
  getItemDate: (item: T) => Date;
  columns: number;
}

interface GroupedItems<T> {
  prettyDate: string;
  fullDate: Date;
  items: T[];
  isVisible: boolean;
}

function groupItemsByMonth<T>(
  items: T[],
  getItemDate: (item: T) => Date
): Omit<GroupedItems<T>, "isVisible">[] {
  const grouped = items.reduce((acc, item) => {
    const date = getItemDate(item);
    const monthKey = format(date, "yyyy-MM");

    if (!acc[monthKey]) {
      acc[monthKey] = {
        prettyDate: format(date, "MMMM yyyy"),
        fullDate: date,
        items: [],
      };
    }

    acc[monthKey].items.push(item);
    return acc;
  }, {} as Record<string, Omit<GroupedItems<T>, "isVisible">>);

  return Object.values(grouped).sort(
    (a, b) => b.fullDate.getTime() - a.fullDate.getTime()
  );
}

function MonthlyList<T>({
  items,
  renderItem,
  getItemDate,
  columns,
}: MonthlyListProps<T>) {
  const [monthGroups, setMonthGroups] = useState<GroupedItems<T>[]>(() => {
    const groups = groupItemsByMonth(items, getItemDate);
    return groups.map((group) => ({
      ...group,
      isVisible: true,
    }));
  });

  function toggleMonthVisibility(monthIndex: number) {
    setMonthGroups((prev) =>
      prev.map((group, idx) =>
        idx === monthIndex ? { ...group, isVisible: !group.isVisible } : group
      )
    );
  }

  useEffect(() => {
    const groups = groupItemsByMonth(items, getItemDate);
    setMonthGroups(groups.map((group) => ({ ...group, isVisible: true })));
  }, [items, getItemDate]);

  return (
    <div className="bg-[#E0E8E8] p-4 md:p-6 mb-12 space-y-6">
      {monthGroups.map((month, monthIndex) => (
        <div key={month.fullDate.toString()} className="bg-[#F0F4F4] p-4">
          <button
            onClick={() => toggleMonthVisibility(monthIndex)}
            className="w-full flex items-center justify-between text-[#126798] mb-4"
          >
            <h4 className="text-[15px] font-medium m-0">{month.prettyDate}</h4>
            <ChevronDownIcon
              className={`w-5 h-5 transition-transform duration-200 ${
                !month.isVisible ? "rotate-180" : ""
              }`}
            />
          </button>

          <div className={`${!month.isVisible ? "hidden" : ""}`}>
            <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-6`}>
              {month.items.map((item, index) => (
                <div key={index} className="w-full">
                  {renderItem(item)}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default MonthlyList;
